import {
  Card,
  Group,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { dateFormat } from '@/index';
import { TbTrashXFilled } from 'react-icons/tb';
import { useRef } from 'react';
import useDeleteEmployeeAttachment from '@/hooks/employee/useDeleteEmployeeAttachment';
import useCreateEmployeeAttachment from '@/hooks/employee/useCreateEmployeeAttachment';
import useEmployeeAttachements from '@/hooks/employee/useEmployeeAttachments';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { type CreateEmployeeAttachment } from '@/schemas/API/POST/employee/attachment/request';
import { downloadFileWithBearerToken } from '@/utils/functions';

const EmployeeEducationAttachmentsCard = () => {
  const { attachments, error } = useEmployeeAttachements();
  const { createEmployeeAttachment } = useCreateEmployeeAttachment();
  const { deleteEmployeeAttachment } = useDeleteEmployeeAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const newAttachment: CreateEmployeeAttachment = { file, name: file.name };
    void createEmployeeAttachment(newAttachment);
  };

  // TODO: add remove confirmation modal?
  const handleRemove = (id: number) => {
    void deleteEmployeeAttachment(id);
  };

  if (error) return <SectionErrorNotification />;
  if (!attachments) return <SectionLoadingOverlay />;

  return (
    <Card padding="md" radius="md" withBorder>
      <Group justify="space-between">
        <Group>
          <Text fw={700} size="large">
            Přílohy
          </Text>
        </Group>
        <UnstyledButton data-testid="add attachment" onClick={handleFileUpload}>
          <input
            data-testid="file input"
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <IconPlus />
        </UnstyledButton>
      </Group>
      <ScrollArea>
        <Stack className="mt-3">
          {attachments.content.map(a => (
            <div key={a.id}>
              <Group justify="space-between">
                {a.name}
                <Group justify="flex-end">
                  {dayjs(a.createdAt).format(dateFormat)}
                  <UnstyledButton
                    data-testid="download attachment"
                    onClick={async () =>
                      await downloadFileWithBearerToken(a.url)
                    }
                  >
                    <IconDownload size="20" />
                  </UnstyledButton>
                  <UnstyledButton
                    data-testid="delete attachment"
                    onClick={() => handleRemove(a.id)}
                  >
                    <TbTrashXFilled size="25" />
                  </UnstyledButton>
                </Group>
              </Group>
              {/* Grey line */}
              <hr className="h-px my-3 bg-gray-200 border-0 dark:bg-gray-700" />
            </div>
          ))}
        </Stack>
      </ScrollArea>
    </Card>
  );
};

export default EmployeeEducationAttachmentsCard;
